import { useEffect, useState, type FormEvent } from 'react'
import { supabase } from '@/lib/supabase'
import { insertEvent } from '@/lib/events'
import { pt } from '@/i18n/pt-PT'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { FormField } from '@/components/ui/FormField'

type Enrollment = {
  factorId: string
  qrSvg: string
  secret: string
} | null

type MfaEnrollProps = {
  onVerified: () => void
}

// One-time TOTP enrollment. Supabase returns `qr_code` as an SVG markup
// string — injected via dangerouslySetInnerHTML because it's our own auth
// provider's output, not user content.
export function MfaEnroll({ onVerified }: MfaEnrollProps) {
  const [enrollment, setEnrollment] = useState<Enrollment>(null)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function start() {
      const { data, error: enrollErr } = await supabase.auth.mfa.enroll({ factorType: 'totp' })
      if (cancelled) return
      if (enrollErr || !data) {
        setError(enrollErr?.message ?? 'Enrollment failed')
        return
      }
      if (data.type !== 'totp') {
        setError('Unexpected factor type')
        return
      }
      setEnrollment({
        factorId: data.id,
        qrSvg: data.totp.qr_code,
        secret: data.totp.secret,
      })
    }
    start()
    return () => {
      cancelled = true
    }
  }, [])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!enrollment) return
    setError(null)
    setSubmitting(true)
    const { data: challenge, error: chErr } = await supabase.auth.mfa.challenge({
      factorId: enrollment.factorId,
    })
    if (chErr || !challenge) {
      setSubmitting(false)
      setError(pt.auth.mfaInvalidCode)
      return
    }
    const { error: verifyErr } = await supabase.auth.mfa.verify({
      factorId: enrollment.factorId,
      challengeId: challenge.id,
      code,
    })
    setSubmitting(false)
    if (verifyErr) {
      setError(pt.auth.mfaInvalidCode)
      return
    }
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (user) {
      await insertEvent({
        type: 'user.mfa_challenge',
        subjectType: 'user',
        subjectId: user.id,
        payload: { outcome: 'enrolled_and_verified' },
      })
    }
    onVerified()
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas p-8">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md space-y-5 rounded-lg border border-DEFAULT bg-surface p-8 shadow-card"
      >
        <div>
          <h1 className="text-[18px] font-semibold text-gray-900">
            {pt.auth.mfaEnrollTitle}
          </h1>
          <p className="mt-1 text-[12px] text-gray-500">{pt.auth.mfaEnrollIntro}</p>
        </div>
        {enrollment ? (
          <>
            <div
              className="mx-auto h-48 w-48 rounded-md border border-DEFAULT bg-surface p-3"
              dangerouslySetInnerHTML={{ __html: enrollment.qrSvg }}
            />
            <div className="rounded-md border border-DEFAULT bg-surface-muted p-3 text-[11px] text-gray-700">
              <p className="text-gray-500">{pt.auth.mfaEnrollSecret}</p>
              <p className="mt-1 break-all font-mono text-[12px] text-gray-900">
                {enrollment.secret}
              </p>
            </div>
            <FormField id="mfa-code" label={pt.auth.mfaCodeLabel} required>
              <Input
                id="mfa-code"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                required
                className="text-center font-mono text-[16px] tracking-[0.4em]"
              />
            </FormField>
            {error && (
              <p className="text-[11.5px] text-red-700" role="alert">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={submitting || code.length !== 6}
              className="h-9 w-full text-[13px]"
            >
              {pt.auth.mfaVerify}
            </Button>
          </>
        ) : (
          <p className="text-[12px] text-gray-500">{pt.auth.loading}</p>
        )}
      </form>
    </main>
  )
}
